import type {
  ChoiceChange,
  ChoicesState,
  EventDetailsResponse,
  EventMeResponse,
  FinishResult,
  FinishState,
  InvoiceResponse,
  MedicalDraft,
  MedicalItem,
  PromoPreview,
  PromoResult,
  SubmitInput,
  SubmitResult,
} from './types';
import type { LearnedRelationship } from './relationships';
import { getMpWidgetToken } from './campus';
import { mpFetch } from '@church/nextjs-ui/website/mp-session';

// Vite bakes these at build time via `define` in vite.config.ts. The slug is
// what lets apps/api attribute writes to this widget's own service identity
// (see the public-write path in apps/api/src/middleware.ts).
const BAKED_API_KEY = process.env.CHURCH_HUB_API_KEY ?? '';
const BAKED_APP_SLUG = process.env.CHURCH_HUB_APP_SLUG ?? '';

function headers(json = false): Record<string, string> {
  const h: Record<string, string> = {};
  if (BAKED_API_KEY) h['x-api-key'] = BAKED_API_KEY;
  if (BAKED_APP_SLUG) h['x-app-slug'] = BAKED_APP_SLUG;
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

/** Every call carries the sign-in through mpFetch, which renews it when it runs out. */
function call(
  url: string,
  init: { method?: string; body?: string; signal?: AbortSignal } = {}
): Promise<Response> {
  return mpFetch(url, { ...init, headers: headers(init.body !== undefined) });
}

function base(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/$/, '');
}

const RETRY_DELAYS_MS = [1500, 3000];

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
  }
}

/**
 * GET with two retries on a 5xx or network error: this runs on a public page,
 * and a brief edge hiccup should not show visitors an error card.
 */
async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    // attempt is 1..RETRY_DELAYS_MS.length here, so the delay exists.
    if (attempt > 0) await sleep(RETRY_DELAYS_MS[attempt - 1] as number, signal);
    try {
      const res = await call(url, { signal });
      if (res.ok) return (await res.json()) as T;
      lastError = new ApiError(`${url} responded ${res.status}`, res.status);
      if (res.status < 500) break; // a 4xx will not fix itself
    } catch (err) {
      if (signal?.aborted) throw err;
      lastError = err;
    }
  }
  throw lastError;
}

/** PUT and DELETE, which carry a body or none. Never retried, like POST. */
async function sendJson<T>(
  url: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown
): Promise<T> {
  const res = await call(url, {
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const data = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!res.ok) throw new ApiError(data?.error || `${url} responded ${res.status}`, res.status);
  return data as T;
}

/** POST once. Writes are never retried blind: a duplicate registration is worse than an error. */
async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await call(url, { method: 'POST', body: JSON.stringify(body) });
  const data = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!res.ok) throw new ApiError(data?.error || `${url} responded ${res.status}`, res.status);
  return data as T;
}

export const is404 = (err: unknown) => err instanceof ApiError && err.status === 404;

/**
 * The sign-in ran out and could not be renewed. The page has already been asked
 * to sign them in (MP_SIGN_IN_NEEDED), so this is not an error to show.
 */
export const isSignInNeeded = (err: unknown) => err instanceof ApiError && err.status === 401;

/** What to show for a failed save: nothing when it only needs a sign-in. */
export const errorText = (err: unknown, fallback: string): string | null =>
  isSignInNeeded(err) ? null : err instanceof Error ? err.message : fallback;

/**
 * `here` is this page's own address, sent so that an event whose
 * External_Registration_URL points AT this page is not read as registering
 * somewhere else. Without it the widget would offer a link back to itself.
 */
/**
 * `ref` is an event id or a slug — the API resolves either, and the response
 * carries the numeric id for every call after this one.
 *
 * ENCODED, BECAUSE A SLUG CAN NOW CONTAIN A SLASH. `baptism/lake-orion` put
 * straight into the path makes `/api/events/baptism/lake-orion/details`, which
 * is four segments where the route has three, so it matches nothing and the
 * page says it cannot find the event. `flannel-fest` kept working throughout,
 * which is exactly the shape of the bug: one segment fits, two do not.
 * Encoded it is a single segment again and Next hands the route the slash
 * back. Verified against production both ways.
 */
export function fetchEventDetails(
  apiBaseUrl: string,
  eventId: number | string,
  signal?: AbortSignal,
  here?: string
): Promise<EventDetailsResponse> {
  const suffix = here ? `?here=${encodeURIComponent(here)}` : '';
  const ref = encodeURIComponent(String(eventId));
  return getJson(`${base(apiBaseUrl)}/api/events/${ref}/details${suffix}`, signal);
}

/** The signed-in half. Null when there is no token or it is no longer good. */
export async function fetchEventMe(
  apiBaseUrl: string,
  eventId: number,
  signal?: AbortSignal
): Promise<EventMeResponse | null> {
  if (!getMpWidgetToken()) return null;
  try {
    const res = await call(`${base(apiBaseUrl)}/api/events/${eventId}/me`, { signal });
    if (!res.ok) return null;
    return (await res.json()) as EventMeResponse;
  } catch (err) {
    if (signal?.aborted) throw err;
    return null;
  }
}

export function previewPromos(
  apiBaseUrl: string,
  eventId: number,
  registrants: { key: string; contactId: number | null }[],
  typedCode: string | null
): Promise<PromoPreview> {
  return postJson(`${base(apiBaseUrl)}/api/events/${eventId}/promos/preview`, {
    registrants,
    typedCode,
  });
}

export function checkPromo(
  apiBaseUrl: string,
  eventId: number,
  code: string,
  contactIds: number[]
): Promise<PromoResult> {
  return postJson(`${base(apiBaseUrl)}/api/events/${eventId}/promos/check`, { code, contactIds });
}

export function submitRegistration(apiBaseUrl: string, input: SubmitInput): Promise<SubmitResult> {
  return postJson(`${base(apiBaseUrl)}/api/events/${input.eventId}/register`, input);
}

/**
 * A question for whoever runs the event. The note is relayed server-side —
 * their address is never shipped to the browser.
 */
export function askQuestion(
  apiBaseUrl: string,
  eventId: number,
  note: { name: string; email: string; message: string; contactId?: number; church?: string }
): Promise<{ ok: true }> {
  return postJson(`${base(apiBaseUrl)}/api/events/${eventId}/contact`, note);
}

/**
 * A relationship the household just told us about, written to MP's records
 * rather than kept in one form's answers. Fire and forget on purpose: the
 * registration must not wait on it, and a failure costs the question being
 * asked again next year — which is what happens today anyway.
 */
export function saveRelationship(
  apiBaseUrl: string,
  eventId: number,
  fact: LearnedRelationship
): Promise<{ ok: true }> {
  return postJson(`${base(apiBaseUrl)}/api/events/${eventId}/relationship`, fact);
}

/**
 * A gender a question had to ask for, saved as soon as it is answered. The
 * server writes it only where MP has none. Fire and forget, like the
 * relationship above: the answer on the form stands either way.
 */
export function saveGender(
  apiBaseUrl: string,
  eventId: number,
  contactId: number,
  gender: 'male' | 'female'
): Promise<{ ok: true }> {
  return postJson(`${base(apiBaseUrl)}/api/events/${eventId}/gender`, { contactId, gender });
}

/**
 * Somebody being added from the registration form.
 *
 * NOT fire and forget, unlike the relationship above: the person reading the
 * page is waiting to tick this person's name, so the answer decides what they
 * see. `added` gives back a contact id to register; `pending` means MP already
 * held them and their family has to agree first, and says nothing else about
 * them on purpose.
 */
export function addPerson(
  apiBaseUrl: string,
  eventId: number,
  person: AddPersonBody
): Promise<AddPersonResult> {
  return postJson(`${base(apiBaseUrl)}/api/events/${eventId}/household`, person);
}

/** Someone archived out of the viewer's household, who can be added back. */
export interface FormerMemberLite {
  contactId: number;
  firstName: string;
  name: string;
  imageUrl: string | null;
}

/** The viewer's archived former household members (empty when signed out). */
/** A prayer request from an experience's prayer card (women's conference). */
export async function sendPrayer(
  apiBaseUrl: string,
  eventId: number,
  prayer: {
    request: string;
    name?: string;
    email?: string;
    shareable?: boolean;
    shareName?: boolean;
  }
): Promise<{ ok: true }> {
  return postJson(`${base(apiBaseUrl)}/api/events/${eventId}/prayer`, prayer);
}

export async function fetchFormerMembers(
  apiBaseUrl: string,
  eventId: number,
  signal?: AbortSignal
): Promise<FormerMemberLite[]> {
  const data = await getJson<{ formerMembers?: FormerMemberLite[] }>(
    `${base(apiBaseUrl)}/api/events/${eventId}/household/former`,
    signal
  );
  return data.formerMembers ?? [];
}

/** Add an archived former member back instead of creating them again. */
export function bringBackPerson(
  apiBaseUrl: string,
  eventId: number,
  contactId: number
): Promise<{ outcome: 'added'; contactId: number }> {
  return postJson(`${base(apiBaseUrl)}/api/events/${eventId}/household/former/${contactId}`, {});
}

export interface AddPersonBody {
  firstName: string;
  lastName: string;
  nickname?: string | null;
  middleName?: string | null;
  relationshipId: number | null;
  gender: 'male' | 'female' | null;
  addToHousehold: boolean;
  dateOfBirth?: string | null;
  email?: string | null;
  phone?: string | null;
  workPhone?: string | null;
  positionId?: number | null;
  maritalStatusId?: number | null;
  shirtSizeId?: number | null;
  /** A minor living elsewhere: their parent. Null when the viewer does not know them. */
  parent?: {
    firstName: string;
    lastName: string;
    phone: string;
    email?: string | null;
    role: 'mom' | 'dad' | 'guardian';
  } | null;
}

export type AddPersonResult =
  | { outcome: 'added'; contactId: number; firstName: string; lastName: string }
  | { outcome: 'pending'; firstName: string; lastName: string };

export function fetchInvoice(
  apiBaseUrl: string,
  guid: string,
  claimedSuccess: boolean,
  signal?: AbortSignal
): Promise<InvoiceResponse> {
  const q = claimedSuccess ? '?claimed=1' : '';
  return getJson(`${base(apiBaseUrl)}/api/invoices/${encodeURIComponent(guid)}${q}`, signal);
}

/** Mint a fresh token for the same invoice; the answer is where to send the browser. */
export function payInvoice(
  apiBaseUrl: string,
  guid: string,
  returnUrl: string,
  amount?: number
): Promise<{ url: string; amount: number; balance: number }> {
  return postJson(`${base(apiBaseUrl)}/api/invoices/${encodeURIComponent(guid)}/pay`, {
    returnUrl,
    ...(amount ? { amount } : {}),
  });
}

/**
 * A MEDICAL ROW CORRECTED WHERE IT IS READ
 * ========================================
 *
 * The form shows what MP already holds so a family confirms rather than
 * retypes — and the person reading it is the one who knows the allergy is
 * misspelt or the inhaler is long gone. These edit the real
 * `Contact_Medical_Items` row, the same one /profile edits, rather than
 * patching this registration's answer and leaving the record wrong.
 *
 * NOT fire and forget. The row is on screen; if the write fails the row must
 * go back to what it was rather than lie about having changed.
 */
export function updateMedicalItem(
  apiBaseUrl: string,
  eventId: number,
  contactId: number,
  itemId: number,
  patch: Partial<Omit<MedicalItem, 'id' | 'since' | 'summary'>>
): Promise<{ item: MedicalItem }> {
  return sendJson(`${base(apiBaseUrl)}/api/events/${eventId}/medical/${itemId}`, 'PUT', {
    contactId,
    ...patch,
  });
}

/**
 * File a NEW row, the same one /profile would file.
 *
 * An allergy typed into a registration form used to live in the form answer
 * and nowhere else, so the same parent typed the same allergy again at the
 * next event and the church's record of it stayed empty. Which quietly undid
 * the backfill that put years of those answers onto the person in the first
 * place.
 *
 * The draft stays in the answer either way. If this write fails the family
 * has still told us, in the place a leader reads — it just did not become a
 * row, and nothing is lost.
 */
export function addMedicalItem(
  apiBaseUrl: string,
  eventId: number,
  contactId: number,
  draft: MedicalDraft
): Promise<{ item: MedicalItem }> {
  return sendJson(`${base(apiBaseUrl)}/api/events/${eventId}/medical`, 'POST', {
    contactId,
    ...draft,
  });
}

/** Soft: MP end-dates the row. A medication somebody stopped is history, not a mistake. */
export function removeMedicalItem(
  apiBaseUrl: string,
  eventId: number,
  contactId: number,
  itemId: number
): Promise<{ ok: true }> {
  return sendJson(
    `${base(apiBaseUrl)}/api/events/${eventId}/medical/${itemId}?contactId=${contactId}`,
    'DELETE'
  );
}

/** The household's places on an event and their picks; null when signed out. */
export async function fetchChoices(
  apiBaseUrl: string,
  eventId: number,
  signal?: AbortSignal
): Promise<ChoicesState | null> {
  if (!getMpWidgetToken()) return null;
  return getJson<ChoicesState>(`${base(apiBaseUrl)}/api/events/${eventId}/choices`, signal);
}

/** Change one pick or one answer. Answers the household's choices as they now stand. */
export function saveChoice(
  apiBaseUrl: string,
  eventId: number,
  change: ChoiceChange
): Promise<{ ok: boolean; note?: string; state: ChoicesState }> {
  return postJson(`${base(apiBaseUrl)}/api/events/${eventId}/choices`, change);
}

/** A place offered to someone else (apps/api lib/events/transfers.ts). */
export interface PlaceTransfer {
  id: number;
  eventParticipantId: number;
  status: 'offered' | 'approved' | 'settled' | 'withdrawn' | 'declined' | 'failed';
  toName: string | null;
  offeredAt: string;
}

export interface TransfersState {
  /** Events.Allow_Registration_Transfers. */
  allowed: boolean;
  /** Still before the event starts. */
  open: boolean;
  closesAt: string;
  transfers: PlaceTransfer[];
}

export function fetchTransfers(
  apiBaseUrl: string,
  eventId: number,
  signal?: AbortSignal
): Promise<TransfersState> {
  return getJson(`${base(apiBaseUrl)}/api/events/${eventId}/transfers`, signal);
}

export function offerPlace(
  apiBaseUrl: string,
  eventId: number,
  offer: {
    eventParticipantId: number;
    toContactId?: number | null;
    toName?: string | null;
    toEmail?: string | null;
    note?: string | null;
  }
): Promise<{ ok: true; id: number }> {
  return postJson(`${base(apiBaseUrl)}/api/events/${eventId}/transfers`, offer);
}

export function withdrawPlaceOffer(
  apiBaseUrl: string,
  eventId: number,
  transferId: number
): Promise<{ ok: true }> {
  return sendJson(`${base(apiBaseUrl)}/api/events/${eventId}/transfers/${transferId}`, 'DELETE');
}

/** The people on an invoice registered at the door, with their questions' answers so far. */
export function fetchFinish(
  apiBaseUrl: string,
  invoiceGuid: string,
  signal?: AbortSignal
): Promise<FinishState> {
  return getJson<FinishState>(
    `${base(apiBaseUrl)}/api/invoices/${encodeURIComponent(invoiceGuid)}/finish`,
    signal
  );
}

/** Their answers, saved. Once: a write is never retried blind. */
export function saveFinish(
  apiBaseUrl: string,
  invoiceGuid: string,
  people: { eventParticipantId: number; answers: Record<string, string> }[]
): Promise<FinishResult> {
  return postJson<FinishResult>(
    `${base(apiBaseUrl)}/api/invoices/${encodeURIComponent(invoiceGuid)}/finish`,
    { people }
  );
}
